import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./Navbar.css";
import logo from "../../assets/kinglogo.png";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [navRef]);

  const navItems = [
    { path: "/", label: "HOME" },
    { path: "/about-me", label: "ABOUT" },
    { path: "/projects", label: "PROJECTS" },
    { path: "/contact-me", label: "CONTACT" },
  ];

  return (
    <motion.nav
      className={`navbar ${scrolled ? "scrolled" : ""}`}
      ref={navRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", damping: 10 }}
    >
      <div className="navbar-container">
        {/* Logo */}
        <motion.div
          className="logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link to="/" className="navbar-logo">
            <motion.img
              src={logo}
              alt="Logo"
              id="logo"
              animate={{
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                repeat: Infinity,
                repeatType: "mirror",
                duration: 5,
                ease: "easeInOut",
              }}
            />
          </Link>
        </motion.div>

        {/* Desktop Navigation */}
        <ul className="desktop-nav">
          {navItems.map((item, index) => (
            <motion.li
              key={item.label}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              {item.path.startsWith("#") ? (
                <a
                  href={item.path}
                  className="nav-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                  <motion.span
                    className="nav-underline"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                  />
                </a>
              ) : (
                <Link
                  to={item.path}
                  className="nav-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                  <motion.span
                    className="nav-underline"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                  />
                </Link>
              )}
            </motion.li>
          ))}
        </ul>
      </div>
      {/* Mobile Menu Button */}
      <motion.div
        className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        whileTap={{ scale: 0.9 }}
      >
        <motion.span
          className="bar"
          animate={
            isMobileMenuOpen
              ? { rotate: 45, y: 7, backgroundColor: "#1DA1F2" }
              : { rotate: 0, y: 0, backgroundColor: "#E7E9EA" }
          }
        />
        <motion.span
          className="bar"
          animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
        />
        <motion.span
          className="bar"
          animate={
            isMobileMenuOpen
              ? { rotate: -45, y: -7, backgroundColor: "#1DA1F2" }
              : { rotate: 0, y: 0, backgroundColor: "#E7E9EA" }
          }
        />
      </motion.div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
          >
            <ul className="mobile-nav-links">
              {navItems.map((item) => (
                <motion.li
                  key={item.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {item.path.startsWith("#") ? (
                    <a
                      href={item.path}
                      className="mobile-nav-item"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="mobile-nav-item"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
